/* eslint-disable max-statements, react/jsx-no-constructed-context-values */
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/outline";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { addDays, formatISO, parseISO } from "date-fns";
import { eventSortFn, isCourseInFuture, isNotCourse } from "../helpers/event";
import { isArray } from "underscore";

import AttendeeAddGuestModal from "../components/CoursesList/AttendeeAddGuestModal";
import CancelCourseRegistrationModal from "./CancelCourseRegistrationModal";
import { useAuth } from "./wp.auth.service";
import { useNotifications } from "../components/NotificationsContext";
const CourseRegistrationContext = React.createContext(null);
const maxCartDayAge = 1;

const regCoursesSlug = "registeredCourses";
const regCoursesTSSlug = "cartValidUntil";

// eslint-disable-next-line react/prop-types
export const CourseRegistrationProvider = ({ children }) => {
	const { user } = useAuth();
	const { addNotification } = useNotifications();
	const [registeredCourses, setRegisteredCourses] = useState([]);
	const [regToCancel, setRegToCancel] = useState(null);
	const [courseToAddGuest, setCourseToAddGuest] = useState(null);
	const [guests, setGuests] = useState({});

	const updateTS = useCallback(() => {
		window.localStorage.setItem(regCoursesTSSlug, formatISO(addDays(new Date(), maxCartDayAge)));
	}, []);

	/**
	 * Load Locally Stored Registrations
	 */
	useEffect(() => {
		const storedClasses = window.localStorage.getItem(regCoursesSlug);
		const cartValidUntil = parseISO(window.localStorage.getItem(regCoursesTSSlug));

		if (new Date() < cartValidUntil) {
			let coursesWithDates = [];
			if (storedClasses && JSON.parse(storedClasses) instanceof Array) {
				const courses = JSON.parse(storedClasses);
				coursesWithDates = courses.map((course) => {
					course.start = new Date(course.start);
					course.end = new Date(course.end);
					return course;
				}).filter(isCourseInFuture);
				setRegisteredCourses(coursesWithDates);
			}
		} else {
			window.localStorage.removeItem(regCoursesSlug);
			window.localStorage.removeItem(regCoursesTSSlug);
		}
	}, []);

	/**
	 * Associative Object of Course in Registration List
	 */
	const registeredCoursesObject = useMemo(() => registeredCourses.reduce(
		(prev, cur) => ({
			...prev,
			[cur.id]: true
		}),
		{}
	), [registeredCourses]);

	useEffect(() => {
		window.localStorage.setItem(regCoursesSlug, JSON.stringify(registeredCourses));
	}, [registeredCourses]);

	useEffect(() => {
		setGuests((prev) => {
			for (const courseId in prev) {
				if (!registeredCoursesObject[courseId]) {
					delete prev[courseId];
				}
			}

			return prev;
		});
	}, [registeredCoursesObject]);

	const registeredFor = useCallback((newCourse) => !!registeredCoursesObject[newCourse.id], [registeredCoursesObject]);

	const addCourse = useCallback((course) => {
		let newCourses = [];
		if (isArray(course)) {
			newCourses = [...course, ...course.series?.courses?.filter(isCourseInFuture).filter(isNotCourse(course)) || []];
		} else {
			newCourses = [course, ...course.series?.courses?.filter(isCourseInFuture).filter(isNotCourse(course)) || []];
		}

		newCourses = newCourses.filter((newCourse) => !registeredFor(newCourse));

		const newCourseIds = newCourses.map(({ id }) => parseInt(id, 10));
		setRegisteredCourses((courses) => [
			...courses.filter(({ id }) => newCourseIds.indexOf(id) === -1),
			...newCourses
		].filter(isCourseInFuture).sort(eventSortFn));
		updateTS();

		if (newCourses.length > 0) {
			let message = `Added "${newCourses[0].title}" to your registration list.`;
			if (newCourses.length > 1) {
				if (course.series) {
					message = `Added ${newCourses.length} classes from the series "${course.series.title}" to your registration list.`;
				} else {
					message = `Added ${newCourses.length} classes to your registration list.`;
				}
			}

			addNotification({
				className: "text-neutral1 bg-green text-white",
				icon: <PlusCircleIcon className="i me-2" />,
				message,
				type: "green"
			});
		}
	}, []);

	const removeGuest = useCallback((courseId) => {
		setGuests((prev) => {
			const newGuests = { ...prev };
			delete newGuests[courseId];
			return newGuests;
		});
	}, [setGuests]);

	const removeCourse = useCallback(async (course) => {
		let removeCourses = [];
		if (isArray(course)) {
			removeCourses = course;
		} else {
			removeCourses = [course];
		}
		const seriesIds = [];
		const removeCoursesIds = removeCourses.map(({ id, series }) => {
			if (series?.id) {
				seriesIds.push(series.id);
			}
			return parseInt(id, 10);
		});

		await setRegisteredCourses((prevCourses) => {
			const beforeCount = prevCourses.length;
			const filteredCourses = prevCourses.
				filter((prevCourse) => removeCoursesIds.indexOf(prevCourse.id) === -1 &&
					seriesIds.indexOf(prevCourse.series?.id) === -1);
			const numberRemoved = beforeCount - filteredCourses.length;

			// eslint-disable-next-line no-constant-condition
			if (numberRemoved > 0 && false) {
				const plurarity = numberRemoved > 1 ? "classes" : "class";
				addNotification({
					className: "text-neutral1 bg-warning text-neutral1",
					icon: <MinusCircleIcon className="i me-2" />,
					message: `Removed ${numberRemoved} ${plurarity} from your registration list.`,
					type: "green"
				});
			}

			return filteredCourses;
		});

		removeGuest(removeCoursesIds[0] || removeCoursesIds);

		updateTS();
	}, []);

	const removeCourses = useCallback((courses) => removeCourse(courses), []);

	const setCoursesChronologically = useCallback((courses) => {
		if (typeof courses === "function") {
			setRegisteredCourses((prevCourses) => courses(prevCourses).sort(eventSortFn));
		} else if (isArray(courses)) {
			setRegisteredCourses(courses.sort(eventSortFn));
		}
	}, []);

	const handleCancel = useCallback(() => {
		setRegToCancel(null);
	}, []);

	const handleCancelAddGuest = useCallback(() => {
		setCourseToAddGuest(null);
	}, [setCourseToAddGuest]);

	const addGuest = useCallback((courseId, guest) => {
		setGuests((prev) => ({
			...prev,
			[courseId]: guest
		}));
	}, [setGuests]);

	const providerReturn = useMemo(() => ({
		addCourse,
		addGuest,
		cancelRegistration: setRegToCancel,
		guests,
		registeredCourses,
		registeredFor,
		removeCourse,
		removeCourses,
		removeGuest,
		setRegisteredCourses: setCoursesChronologically,
		setCourseToAddGuest
	}), [registeredCourses, user, guests]);

	return (
		<CourseRegistrationContext.Provider value={providerReturn}>
			{children}
			<AttendeeAddGuestModal onCancel={handleCancelAddGuest} show={courseToAddGuest} />
			<CancelCourseRegistrationModal course={regToCancel} onCancel={handleCancel} show={!!regToCancel} />
		</CourseRegistrationContext.Provider>
	);
};

export const useCourseRegistration = () => React.useContext(CourseRegistrationContext);
